import { useMemo } from 'react';
import { AlertTriangle, ArrowRightLeft, CalendarCheck2, Clock3, MapPinned, ShieldAlert, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import { ATTENDANCE_STATUS_LABELS, calculateSegmentHours, formatAttendanceStatusLabel, formatShortDate, isWorkedAttendanceStatus } from './payrollAttendanceUtils';

const STATUS_OPTIONS = Object.entries(ATTENDANCE_STATUS_LABELS).map(([value, label]) => ({ value, label }));

export const PayrollAttendanceInspector = ({
  selectedEmployee,
  selectedDateKey,
  selectedCellState,
  attendanceForm,
  setAttendanceForm,
  transferForm,
  setTransferForm,
  currentRecord,
  currentAbsence,
  currentShift,
  currentAllocation,
  employeeAllocations,
  selectedIssues,
  aggregationPreview,
  aggregationLoading,
  locations,
  canManagePayroll,
  canApproveSwap,
  onIssueAction,
  onSaveAttendance,
  onApproveSwap,
  onAuthorizeOvertime,
  onDeleteAttendance,
  onSaveTransfer,
  onDeleteTransfer,
}) => {
  const storeOptions = locations.filter((location) => (location?.type ? location.type === 'store' : true) && location?.is_active !== false);
  const issueTone = selectedIssues.some((issue) => issue.severity === 'error') ? 'bg-red-50 text-red-700 border-red-200' : 'bg-orange-50 text-orange-700 border-orange-200';
  const workedStatus = isWorkedAttendanceStatus(attendanceForm?.status);
  const currentSwapApproval = currentRecord?.swap_approval || null;
  const currentOvertimeStatus = currentRecord?.overtime_authorization_status || (Number(currentRecord?.overtime_detected_hours || 0) > 0 ? 'registered' : 'not_applicable');
  const employmentLocked = selectedCellState?.status === 'outside_employment_window' || selectedCellState?.readonly;
  const handleIssueActionClick = (issue) => {
    onIssueAction?.(issue);
    const targetSelector = String(issue?.issue_type || '').includes('store')
      ? '[data-testid="payroll-attendance-store-allocation-panel"]'
      : '[data-testid="payroll-attendance-status-panel"]';
    window.setTimeout(() => {
      document.querySelector(targetSelector)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const aggregationCards = useMemo(() => ([
    {
      label: 'Preview pronto',
      value: aggregationLoading ? '...' : aggregationPreview?.preview_ready ? 'Sim' : 'Não',
    },
    {
      label: 'Loja dominante',
      value: aggregationPreview?.allocation_snapshot?.dominant_store_location || aggregationPreview?.allocation_snapshot?.primary_store_location || '—',
    },
    {
      label: 'Dias trabalhados',
      value: aggregationPreview?.attendance_snapshot?.worked_days || aggregationPreview?.attendance_snapshot?.days_worked || 0,
    },
    {
      label: 'Multi-loja',
      value: aggregationPreview?.attendance_snapshot?.multi_store_days || 0,
    },
    {
      label: 'HE autorizadas',
      value: aggregationPreview?.attendance_snapshot?.authorized_overtime_hours || 0,
    },
    {
      label: 'HE pendentes',
      value: aggregationPreview?.attendance_snapshot?.pending_overtime_hours || 0,
    },
  ]), [aggregationLoading, aggregationPreview]);

  return (
    <aside
      className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm xl:sticky xl:top-[116px]"
      data-testid="payroll-attendance-inspector"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <div className="border-b border-slate-200 px-4 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Inspector</p>
        <div className="mt-1 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-extrabold tracking-tight text-slate-950" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="payroll-attendance-inspector-title">
              {selectedEmployee ? (selectedEmployee.full_name || selectedEmployee.name) : 'Seleccionar colaborador'}
            </h3>
            <p className="text-sm text-slate-600" data-testid="payroll-attendance-inspector-meta">
              {selectedEmployee ? `${selectedEmployee.employee_number} · ${selectedDateKey || 'Sem data'} · ${selectedEmployee.store_location || 'Sem loja base'}` : 'Clique numa célula da matriz para editar o dia.'}
            </p>
            {selectedEmployee && selectedCellState?.calendarLabel && selectedCellState?.calendarLabel !== 'Dia normal' && (
              <Badge variant="outline" className="mt-2 rounded-sm border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700" data-testid="payroll-attendance-inspector-calendar-context-badge">
                {selectedCellState.calendarLabel}
              </Badge>
            )}
            {selectedEmployee && employmentLocked && (
              <Badge variant="outline" className="mt-2 rounded-sm border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-700" data-testid="payroll-attendance-inspector-employment-lock-badge">
                {selectedCellState?.lockReason || 'Dia bloqueado por estar fora do vínculo'}
              </Badge>
            )}
          </div>
          {selectedEmployee && (
            <Badge variant="outline" className={cn('rounded-sm px-2 py-1 text-[11px]', issueTone)} data-testid="payroll-attendance-selected-status-badge">
              {selectedCellState?.label || 'Sem estado'}
            </Badge>
          )}
        </div>
      </div>

      <ScrollArea className="h-[calc(100vh-13.5rem)]">
        <div className="space-y-4 p-4">
          <div className="grid gap-2 sm:grid-cols-2" data-testid="payroll-attendance-inspector-status-grid">
            {STATUS_OPTIONS.map((item) => {
              const active = attendanceForm?.status === item.value;
              return (
                <Button
                  key={item.value}
                  type="button"
                  variant="outline"
                  className={cn('h-auto min-h-[58px] justify-start rounded-sm border-slate-200 px-3 py-2 text-left', active && 'border-blue-600 bg-blue-50 text-blue-700')}
                  disabled={employmentLocked}
                  onClick={() => setAttendanceForm((current) => ({
                    ...current,
                    status: item.value,
                    use_segments: isWorkedAttendanceStatus(item.value) ? current.use_segments : false,
                    segments: isWorkedAttendanceStatus(item.value) ? current.segments : current.segments.map((segment) => ({ ...segment, work_start: '', lunch_start: '', lunch_end: '', work_end: '' })),
                  }))}
                  data-testid={`payroll-attendance-status-${item.value}`}
                >
                  <div>
                    <div className="text-sm font-semibold">{item.label}</div>
                    <div className="text-[11px] text-current/70">{item.value}</div>
                  </div>
                </Button>
              );
            })}
          </div>

          <div className="grid gap-2 sm:grid-cols-2" data-testid="payroll-attendance-aggregation-panel">
            {aggregationCards.map((item) => (
              <div key={item.label} className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{item.label}</p>
                <p className="mt-1 text-sm font-semibold text-slate-900" data-testid={`payroll-attendance-aggregation-${item.label.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>{item.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-sm border border-slate-200 bg-white p-3" data-testid="payroll-attendance-current-shift-panel">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Turno aplicável</p>
                <h4 className="text-sm font-semibold text-slate-900">{currentShift?.shift_name || 'Sem turno definido'}</h4>
              </div>
              <Badge variant="outline" className="rounded-sm bg-white" data-testid="payroll-attendance-current-shift-store">
                {currentAllocation?.store_location || selectedEmployee?.store_location || 'Sem loja'}
              </Badge>
            </div>
            <p className="mt-2 text-xs text-slate-600" data-testid="payroll-attendance-current-shift-hours">
              {currentShift ? `${currentShift.work_start} · ${currentShift.lunch_start} · ${currentShift.lunch_end} · ${currentShift.work_end}` : 'Defina um turno no diálogo “Turnos” para melhorar a leitura operacional.'}
            </p>
          </div>

          <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="payroll-attendance-status-panel">
            {employmentLocked && (
              <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="payroll-attendance-employment-lock-banner">
                Este dia está fora da janela real de vínculo. O sistema já não aceita novas horas, presença, turno ou alterações de assiduidade nesta data.
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Estado do dia</p>
                <p className="text-sm text-slate-700">Modelo explícito com até 2 segmentos por data.</p>
              </div>
              {currentRecord && <Badge variant="outline" className="rounded-sm bg-white text-slate-700">Registo existente</Badge>}
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Motivo</Label>
                <Input value={attendanceForm.reason || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, reason: event.target.value }))} data-testid="payroll-attendance-reason-input" />
              </div>
              <div className="space-y-1">
                <Label>Notas</Label>
                <Input value={attendanceForm.notes || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, notes: event.target.value }))} data-testid="payroll-attendance-notes-input" />
              </div>
            </div>

            {workedStatus && (
              <div className="rounded-sm border border-slate-200 bg-white p-3" data-testid="payroll-attendance-segmentation-mode-panel">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Modo de lançamento</p>
                    <p className="text-xs text-slate-500">
                      {attendanceForm.use_segments
                        ? 'Segmentação manual activa para multi-loja, multi-período ou repartição real de horas.'
                        : 'Dia simples: o RH pode guardar sem preencher Segmento 1/2.'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm"
                    onClick={() => setAttendanceForm((current) => ({ ...current, use_segments: !current.use_segments }))}
                    data-testid="payroll-attendance-toggle-segmentation-button"
                  >
                    {attendanceForm.use_segments ? 'Usar dia simples' : 'Adicionar segmentação'}
                  </Button>
                </div>
              </div>
            )}

            {workedStatus && !attendanceForm.use_segments && (
              <div className="rounded-sm border border-slate-200 bg-white p-3" data-testid="payroll-attendance-simple-hours-panel">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Dia simples</p>
                    <p className="text-xs text-slate-500">Registe as horas normais do turno sem abrir Segmento 1/2.</p>
                  </div>
                  <Badge variant="outline" className="rounded-sm bg-slate-50 text-slate-700">Sem segmentação obrigatória</Badge>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1 sm:col-span-2">
                    <Label>Loja base</Label>
                    <Select value={attendanceForm.simple_store_location || 'none'} onValueChange={(value) => setAttendanceForm((current) => ({ ...current, simple_store_location: value === 'none' ? '' : value }))} disabled={employmentLocked}>
                      <SelectTrigger data-testid="payroll-attendance-simple-store-select"><SelectValue placeholder="Seleccionar loja" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccionar</SelectItem>
                        {storeOptions.map((location) => <SelectItem key={location.id || location.name} value={location.name}>{location.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Hora de entrada</Label><Input type="time" value={attendanceForm.work_start || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, work_start: event.target.value }))} data-testid="payroll-attendance-simple-work-start" disabled={employmentLocked} /></div>
                  <div className="space-y-1"><Label>Hora de saída</Label><Input type="time" value={attendanceForm.work_end || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, work_end: event.target.value }))} data-testid="payroll-attendance-simple-work-end" disabled={employmentLocked} /></div>
                  <div className="space-y-1"><Label>Saída almoço</Label><Input type="time" value={attendanceForm.lunch_start || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, lunch_start: event.target.value }))} data-testid="payroll-attendance-simple-lunch-start" disabled={employmentLocked} /></div>
                  <div className="space-y-1"><Label>Entrada almoço</Label><Input type="time" value={attendanceForm.lunch_end || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, lunch_end: event.target.value }))} data-testid="payroll-attendance-simple-lunch-end" disabled={employmentLocked} /></div>
                </div>
              </div>
            )}

            {workedStatus && attendanceForm.use_segments && attendanceForm.segments?.map((segment, index) => (
              <div key={`segment-${index + 1}`} className="rounded-sm border border-slate-200 bg-white p-3" data-testid={`payroll-attendance-segment-${index + 1}-panel`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900">Segmento {index + 1}</p>
                  <Badge variant="outline" className="rounded-sm bg-slate-50 text-slate-700" data-testid={`payroll-attendance-segment-${index + 1}-hours`}>
                    {calculateSegmentHours(segment)}h
                  </Badge>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1 sm:col-span-2">
                    <Label>Loja</Label>
                    <Select value={segment.store_location || 'none'} onValueChange={(value) => setAttendanceForm((current) => ({
                      ...current,
                      segments: current.segments.map((item, itemIndex) => (itemIndex === index ? { ...item, store_location: value === 'none' ? '' : value } : item)),
                    }))} disabled={employmentLocked}>
                      <SelectTrigger data-testid={`payroll-attendance-segment-${index + 1}-store-select`}><SelectValue placeholder="Seleccionar loja" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccionar</SelectItem>
                        {storeOptions.map((location) => <SelectItem key={location.id || location.name} value={location.name}>{location.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Entrada</Label><Input type="time" value={segment.work_start || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, segments: current.segments.map((item, itemIndex) => (itemIndex === index ? { ...item, work_start: event.target.value } : item)) }))} data-testid={`payroll-attendance-segment-${index + 1}-work-start`} disabled={employmentLocked} /></div>
                  <div className="space-y-1"><Label>Saída</Label><Input type="time" value={segment.work_end || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, segments: current.segments.map((item, itemIndex) => (itemIndex === index ? { ...item, work_end: event.target.value } : item)) }))} data-testid={`payroll-attendance-segment-${index + 1}-work-end`} disabled={employmentLocked} /></div>
                  <div className="space-y-1"><Label>Saída almoço</Label><Input type="time" value={segment.lunch_start || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, segments: current.segments.map((item, itemIndex) => (itemIndex === index ? { ...item, lunch_start: event.target.value } : item)) }))} data-testid={`payroll-attendance-segment-${index + 1}-lunch-start`} disabled={employmentLocked} /></div>
                  <div className="space-y-1"><Label>Entrada almoço</Label><Input type="time" value={segment.lunch_end || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, segments: current.segments.map((item, itemIndex) => (itemIndex === index ? { ...item, lunch_end: event.target.value } : item)) }))} data-testid={`payroll-attendance-segment-${index + 1}-lunch-end`} disabled={employmentLocked} /></div>
                </div>
              </div>
            ))}

            {attendanceForm.status === 'troca_de_folga' && (
              <div className="rounded-sm border border-sky-200 bg-sky-50 p-3" data-testid="payroll-attendance-swap-panel">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">Troca de folga</p>
                    <p className="text-sm text-sky-900">Registe o dia original, o dia realmente trabalhado e a compensação.</p>
                  </div>
                  <Badge variant="outline" className="rounded-sm bg-white text-sky-700" data-testid="payroll-attendance-swap-approval-status">
                    {currentSwapApproval?.approver_name ? `Aprovada por ${currentSwapApproval.approver_name}` : 'Pendente'}
                  </Badge>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1"><Label>Dia original</Label><Input type="date" value={attendanceForm.swap_request?.original_day || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, swap_request: { ...current.swap_request, original_day: event.target.value } }))} data-testid="payroll-attendance-swap-original-day" /></div>
                  <div className="space-y-1"><Label>Dia trabalhado</Label><Input type="date" value={attendanceForm.swap_request?.actual_worked_day || selectedDateKey || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, swap_request: { ...current.swap_request, actual_worked_day: event.target.value } }))} data-testid="payroll-attendance-swap-actual-day" /></div>
                  <div className="space-y-1"><Label>Dia compensatório</Label><Input type="date" value={attendanceForm.swap_request?.compensatory_day || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, swap_request: { ...current.swap_request, compensatory_day: event.target.value } }))} data-testid="payroll-attendance-swap-compensatory-day" /></div>
                  <div className="space-y-1"><Label>Motivo da troca</Label><Input value={attendanceForm.swap_request?.reason || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, swap_request: { ...current.swap_request, reason: event.target.value } }))} data-testid="payroll-attendance-swap-reason-input" /></div>
                  <div className="space-y-1 sm:col-span-2"><Label>Notas de aprovação</Label><Textarea rows={2} value={attendanceForm.swap_approval_notes || ''} onChange={(event) => setAttendanceForm((current) => ({ ...current, swap_approval_notes: event.target.value }))} data-testid="payroll-attendance-swap-approval-notes" /></div>
                </div>
                {currentSwapApproval?.approved_at && (
                  <div className="mt-3 rounded-sm border border-sky-200 bg-white px-3 py-2 text-sm text-slate-700" data-testid="payroll-attendance-swap-approval-log">
                    Aprovado em {formatShortDate(currentSwapApproval.approved_at)} por {currentSwapApproval.approver_name || '—'}.
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-sm border-sky-300 text-sky-700 hover:bg-sky-100"
                    onClick={() => onApproveSwap(currentRecord?.id, {
                      original_day: attendanceForm.swap_request?.original_day,
                      actual_worked_day: attendanceForm.swap_request?.actual_worked_day || selectedDateKey,
                      compensatory_day: attendanceForm.swap_request?.compensatory_day,
                      reason: attendanceForm.swap_request?.reason || attendanceForm.reason,
                      approval_notes: attendanceForm.swap_approval_notes,
                    })}
                    disabled={!currentRecord?.id || !canApproveSwap}
                    data-testid="payroll-attendance-swap-approve-button"
                  >
                    Aprovar troca de folga
                  </Button>
                </div>
              </div>
            )}

            {Number(currentRecord?.overtime_detected_hours || 0) > 0 && (
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid="payroll-attendance-overtime-panel">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">Horas extras</p>
                    <p className="text-sm text-amber-900">Detectadas {currentRecord?.overtime_detected_hours || 0}h neste registo.</p>
                  </div>
                  <Badge variant="outline" className="rounded-sm bg-white text-amber-700" data-testid="payroll-attendance-overtime-status-badge">
                    {currentOvertimeStatus}
                  </Badge>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-4 text-sm">
                  <div className="rounded-sm border border-amber-200 bg-white px-3 py-2" data-testid="payroll-attendance-overtime-registered-hours">Registadas: {currentRecord?.overtime_detected_hours || 0}h</div>
                  <div className="rounded-sm border border-amber-200 bg-white px-3 py-2" data-testid="payroll-attendance-overtime-pending-hours">Pendentes: {currentRecord?.overtime_pending_hours || 0}h</div>
                  <div className="rounded-sm border border-amber-200 bg-white px-3 py-2" data-testid="payroll-attendance-overtime-authorized-hours">Autorizadas: {currentRecord?.overtime_authorized_hours || 0}h</div>
                  <div className="rounded-sm border border-amber-200 bg-white px-3 py-2" data-testid="payroll-attendance-overtime-rejected-hours">Rejeitadas: {currentRecord?.overtime_rejected_hours || 0}h</div>
                </div>
                {currentRecord?.overtime_authorization?.approved_at && (
                  <div className="mt-3 rounded-sm border border-amber-200 bg-white px-3 py-2 text-sm text-slate-700" data-testid="payroll-attendance-overtime-audit-log">
                    {currentRecord?.overtime_authorization?.status} por {currentRecord?.overtime_authorization?.approver_name || '—'} em {formatShortDate(currentRecord?.overtime_authorization?.approved_at)}.
                    {currentRecord?.overtime_authorization?.notes ? ` Nota: ${currentRecord.overtime_authorization.notes}` : ''}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button type="button" variant="outline" className="rounded-sm" onClick={() => onAuthorizeOvertime(currentRecord?.id, { status: 'pending', notes: 'Pendente de validação' })} disabled={!canApproveSwap} data-testid="payroll-attendance-overtime-pending-button">Marcar pendente</Button>
                  <Button type="button" variant="outline" className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onAuthorizeOvertime(currentRecord?.id, { status: 'authorized', notes: 'Horas extras autorizadas' })} disabled={!canApproveSwap} data-testid="payroll-attendance-overtime-authorize-button">Autorizar</Button>
                  <Button type="button" variant="outline" className="rounded-sm border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onAuthorizeOvertime(currentRecord?.id, { status: 'rejected', notes: 'Horas extras rejeitadas' })} disabled={!canApproveSwap} data-testid="payroll-attendance-overtime-reject-button">Rejeitar</Button>
                </div>
              </div>
            )}

            {currentAbsence && !currentRecord && (
              <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="payroll-attendance-legacy-absence-banner">
                Existe uma justificação legada para esta data. Ao guardar um estado explícito, o novo registo passará a ser o principal no mapa de assiduidade.
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button type="button" className="rounded-sm bg-slate-950 text-white hover:bg-slate-800" onClick={() => onSaveAttendance(attendanceForm, currentRecord?.id || null)} disabled={!canManagePayroll || employmentLocked} data-testid="payroll-attendance-save-button">
                Guardar {formatAttendanceStatusLabel(attendanceForm.status || 'presente').toLowerCase()}
              </Button>
              {currentRecord && (
                <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" onClick={() => onDeleteAttendance(currentRecord.id)} disabled={!canManagePayroll || employmentLocked} data-testid="payroll-attendance-delete-button">
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Remover registo
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-3 rounded-sm border border-indigo-200 bg-indigo-50 p-3" data-testid="payroll-attendance-store-allocation-panel">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-700">Transferência de loja</p>
                <p className="text-sm text-indigo-900">Gestão visual por período sem impacto no cálculo nesta fase.</p>
              </div>
              {currentAllocation && <Badge variant="outline" className="rounded-sm bg-white text-indigo-700">Activa no dia</Badge>}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Loja</Label>
                <Select value={transferForm.store_location || 'none'} onValueChange={(value) => setTransferForm((current) => ({ ...current, store_location: value === 'none' ? '' : value }))}>
                  <SelectTrigger data-testid="payroll-attendance-transfer-store-select"><SelectValue placeholder="Seleccionar loja" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar</SelectItem>
                    {storeOptions.map((location) => <SelectItem key={location.id || location.name} value={location.name}>{location.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Motivo</Label><Input value={transferForm.transfer_reason} onChange={(event) => setTransferForm((current) => ({ ...current, transfer_reason: event.target.value }))} data-testid="payroll-attendance-transfer-reason-input" /></div>
              <div className="space-y-1"><Label>Data início</Label><Input type="date" value={transferForm.start_date} onChange={(event) => setTransferForm((current) => ({ ...current, start_date: event.target.value }))} data-testid="payroll-attendance-transfer-start-date" /></div>
              <div className="space-y-1"><Label>Data fim</Label><Input type="date" value={transferForm.end_date} onChange={(event) => setTransferForm((current) => ({ ...current, end_date: event.target.value }))} data-testid="payroll-attendance-transfer-end-date" /></div>
            </div>
            <div className="space-y-1">
              <Label>Notas</Label>
              <Textarea rows={2} value={transferForm.notes} onChange={(event) => setTransferForm((current) => ({ ...current, notes: event.target.value }))} data-testid="payroll-attendance-transfer-notes-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" className="rounded-sm bg-indigo-600 text-white hover:bg-indigo-700" onClick={() => onSaveTransfer(transferForm, currentAllocation?.id || null)} disabled={!canManagePayroll} data-testid="payroll-attendance-transfer-save-button">Guardar transferência</Button>
              {currentAllocation && (
                <Button type="button" variant="outline" className="rounded-sm border-red-200 text-red-700 hover:bg-red-50" onClick={() => onDeleteTransfer(currentAllocation.id)} disabled={!canManagePayroll} data-testid="payroll-attendance-transfer-delete-button">
                  <Trash2 className="mr-1.5 h-4 w-4" />
                  Remover transferência activa
                </Button>
              )}
            </div>
            <div className="space-y-2" data-testid="payroll-attendance-store-allocation-timeline">
              {employeeAllocations.length === 0 ? (
                <p className="text-xs text-indigo-800/70">Sem transferências registadas para este colaborador no período.</p>
              ) : employeeAllocations.map((allocation) => (
                <div key={allocation.id} className="flex items-start justify-between gap-2 rounded-sm border border-indigo-200 bg-white px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{allocation.store_location}</p>
                    <p className="text-xs text-slate-600">{formatShortDate(allocation.start_date)} – {formatShortDate(allocation.end_date || allocation.start_date)} · {allocation.transfer_reason || 'Sem motivo'}</p>
                  </div>
                  <MapPinned className="mt-0.5 h-4 w-4 text-indigo-500" />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid="payroll-attendance-pending-issues">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Pendências accionáveis</p>
                <p className="text-sm text-slate-700">Alertas do dia e do colaborador seleccionado.</p>
              </div>
              <Badge variant="outline" className={cn('rounded-sm', issueTone)} data-testid="payroll-attendance-pending-issues-count">
                {selectedIssues.length} aberta(s)
              </Badge>
            </div>
            {selectedIssues.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhuma pendência aberta para este contexto.</p>
            ) : selectedIssues.map((issue) => (
              <div key={issue.id} className={cn('rounded-sm border border-l-4 bg-white px-3 py-2', issue.severity === 'error' ? 'border-l-red-500 border-red-200' : 'border-l-orange-400 border-orange-200')} data-testid={`payroll-attendance-issue-${issue.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{issue.message}</p>
                    <p className="mt-1 text-xs text-slate-600">{issue.resolution_hint || issue.issue_type}</p>
                  </div>
                  <ShieldAlert className={cn('mt-0.5 h-4 w-4', issue.severity === 'error' ? 'text-red-500' : 'text-orange-500')} />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge variant="outline" className="rounded-sm bg-white px-2 py-1 text-[11px]" data-testid={`payroll-attendance-issue-action-${issue.id}`}>
                    {String(issue.issue_type || '').includes('store') ? 'Rever transferência no painel ao lado' : 'Corrigir estado diário neste painel'}
                  </Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-sm"
                    onClick={() => handleIssueActionClick(issue)}
                    data-testid={`payroll-attendance-issue-action-button-${issue.id}`}
                  >
                    {String(issue.issue_type || '').includes('store') ? 'Rever' : 'Corrigir estado'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ScrollArea>
    </aside>
  );
};